// WP-0.7 (S-02): passkey button primary; "use password" reveals password + TOTP
// (AU-B). Rate-limit / lockout messages surface from the shared error envelope (AU-C).
import { startAuthentication } from '@simplewebauthn/browser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { markAuthenticated } from '../lib/session';
import { AuthCard } from '../components/AuthCard';
import { Field, ghostButtonClass, inputClass, primaryButtonClass } from '../components/FormKit';

interface PasswordForm {
  password: string;
  totpCode: string;
}

export function LoginPage(): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordForm>();

  const onAuthed = (): void => {
    markAuthenticated(queryClient, { onboarded: true }); // returning users are onboarded; refetch corrects the rare exception
    navigate('/');
  };

  const passkeyMutation = useMutation({
    mutationFn: async () => {
      const options = await api.post('/api/v1/auth/login/passkey/start');
      const assertion = await startAuthentication({
        optionsJSON: (options as { options: unknown }).options as Parameters<
          typeof startAuthentication
        >[0]['optionsJSON'],
      });
      await api.post('/api/v1/auth/login/passkey/finish', { assertion });
    },
    onSuccess: onAuthed,
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'Passkey sign-in failed');
    },
  });

  const passwordMutation = useMutation({
    mutationFn: (form: PasswordForm) => api.post('/api/v1/auth/login/password', form),
    onSuccess: onAuthed,
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'Sign-in failed');
    },
  });

  return (
    <AuthCard title="Sign in">
      <button
        className={primaryButtonClass}
        disabled={passkeyMutation.isPending}
        onClick={() => {
          setError(null);
          passkeyMutation.mutate();
        }}
      >
        Continue with passkey
      </button>

      {!showPassword && (
        <button
          className={`${ghostButtonClass} mt-3`}
          onClick={() => {
            setShowPassword(true);
          }}
        >
          Use password
        </button>
      )}

      {showPassword && (
        <form
          className="mt-4"
          onSubmit={(e) => {
            void handleSubmit((form) => {
              setError(null);
              passwordMutation.mutate(form);
            })(e);
          }}
        >
          <Field label="Password" htmlFor="password" error={errors.password?.message}>
            <input
              id="password"
              type="password"
              className={inputClass}
              {...register('password', { required: 'Required' })}
            />
          </Field>
          <Field label="Authenticator code" htmlFor="totpCode" error={errors.totpCode?.message}>
            <input
              id="totpCode"
              className={inputClass}
              inputMode="numeric"
              {...register('totpCode', {
                required: 'Required',
                pattern: { value: /^\d{6}$/, message: 'Enter the 6-digit code' },
              })}
            />
          </Field>
          <button
            className={primaryButtonClass}
            disabled={passwordMutation.isPending}
            type="submit"
          >
            Sign in
          </button>
        </form>
      )}

      {error !== null && <p className="mt-3 text-sm text-danger">{error}</p>}
    </AuthCard>
  );
}
